import moment from 'moment';
import * as flights from '../flights';
import * as utils from '../utils';
import * as api from '../api';
import { G2S } from '../g2s';

/**
 * Custom validator for vuelidate.
 */
export function validateChecked(value): boolean {
    return value === true;
}

export function validationEmail(value: string): boolean {
    return utils.String.isEmail(value);
}

/**
 * Custom validator for email or phone input.
 */
export function validateEmailOrPhone(value): boolean {
    if (value != undefined && value) {
        if (!utils.String.isEmail(value)) {
            const replaceRegex = /[^0-9+]/gi;
            const v = value.replace(/[^\d+]/gi, '');

            if (replaceRegex.test(value) || v.length < 11) {
                return false;
            }
        }
    }

    return true;
}

export function validationPassengersBirthday(passengers): boolean {
    if (!passengers[0].validationConstraints.orderFormNewDocumentNumberRuValidationEnabled) {
        return true;
    }

    let hasAdultPassenger = false, hasNoAdultPassenger = false;

    passengers.forEach((passenger) => {
        if (passenger.type !== 'adult') {
            hasNoAdultPassenger = true;
        }
        if (passenger.validationConstraints.minDocumentValidTill.isSameOrAfter(moment(passenger.birthday).add(18, 'years'))) {
            hasAdultPassenger = true;
        }
    });

    return hasAdultPassenger || !hasNoAdultPassenger;
}

/**
 * Custom validator for vuelidate.
 */
export function validatePassengerName(value, parentVm: flights.PassengerModel): boolean {
    const allowCyrillic = parentVm.allowCyrillicName;

    // Min length
    if (value != undefined && value && value.length < 2) {
        return false;
    }

    // Name should have only letters
    let regex = new RegExp(allowCyrillic ? '^[а-яёА-ЯЁa-zA-Z ]{2,}$' : '^[a-zA-Z ]{2,}$', 'gi');
    if (value != undefined && value && !regex.test(value)) {
        return false;
    }

    // Name should have vowels
    regex = new RegExp(allowCyrillic ? '^((?![aeijouyаоэиуыеёюя]).)*$' : '^((?![aeijouy]).)*$', 'gi');
    if (value != undefined && value && regex.test(value)) {
        return false;
    }

    return true;
}

/**
 * Custom validator for vuelidate.
 */
export function validatePassengerNameLast(value, parentVm: flights.PassengerModel): boolean {
    const lastNameConstraint = parentVm.validationConstraints[parentVm.documentType].constraints
        .find((constrain) => constrain.nameProperty === 'LastName');
    if (!value && lastNameConstraint.nameConstraint === 'Required') {
        return false;
    } 

    if (lastNameConstraint.nameConstraint !== 'NotAllowed') {
        const allowCyrillic = parentVm.allowCyrillicName;
        // Min length
        if (value != undefined && value && value.length < 2) {
            return false;
        }

        // Name should have only letters
        let regex = new RegExp(allowCyrillic ? '^[а-яёА-ЯЁa-zA-Z]{2,}$' : '^[a-zA-Z]{2,}$', 'gi');
        if (value != undefined && value && !regex.test(value)) {
            return false;
        }

        regex = new RegExp(allowCyrillic ? '^((?![aeijouyаоэиуыеёюя]).)*$' : '^((?![aeijouy]).)*$', 'gi');
        if (value != undefined && value && regex.test(value)) {
            return false;
        }
    }

    return true;
}

/**
 * Custom validator for mobile phone number.
 */
export function validatePhone(value): boolean {
    // Min length of numbers only
    if (value != undefined && value) {
        const v = value.toString().replace(/[^\d]/gi, '');
        
        if (!v || v.length < 11 || v.length > 16) {
            return false;
        }
    }

    return true;
}

/**
 * Custom validator for mobile phone number.
 */
export function isValidDate(value): boolean {
    if (value === 'Invalid date') {
        return false;
    }

    return true;
}

/**
 * Custom validator for vuelidate.
 */
export function validateDocumentValidTill(value, parentVm): boolean {
    const docPeriodConstrains = parentVm.validationConstraints[parentVm.documentType].constraints
        .find((constrain) => constrain.nameProperty === 'ValidityPeriodOfDocument');
    // parentVm is Passenger
    if (docPeriodConstrains.nameConstraint !== 'NotAllowed') {
        
        if (value) {
            const d = moment(value);

            if (d.isValid()) {
                if (d.isBefore(parentVm.validationConstraints.minDocumentValidTill)) {
                    return false;
                }
            }
        } else {
            if (docPeriodConstrains.nameConstraint === 'Required') {
                return false;
            }
        }
    }

    return true;
}

export function validateDocumentNumber(value, parentVm: flights.PassengerModel): boolean {
    if (!parentVm.validationConstraints.orderFormNewDocumentNumberRuValidationEnabled
        || parentVm.citizenship !== 168) {
        return true;
    }
    if ((parentVm.validationConstraints.minDocumentValidTill.isSameOrAfter(moment(parentVm.birthday).add(14, 'y').add(3, 'M')) || !parentVm.validationConstraints['documentBirthCertificate'] || parentVm.documentType !== 'documentBirthCertificate') && !(/^\d+$/).test(value)) {
        return false;
    } else if (parentVm.documentType === 'documentBirthCertificate' && !(/[0-9IVX]{1,4}[а-яА-Я]{2}[0-9]{6}$/).test(value)) {
        return false;
    }

    if (parentVm.documentType === 'documentPassportRu' && (parentVm.validationConstraints.minDocumentValidTill.isBefore(moment(parentVm.birthday).add(14, 'y')) || value.length !== 10)) {
        return false;
    }

    if (parentVm.documentType === 'documentPassportForeign' && value.length !== 9 && (/[0-9]$/).test(value)) {
        return false;
    }

    return true;
}

export function validateCertificateAge(value: any, parentVm: flights.PassengerModel): boolean {
    if (!parentVm.validationConstraints.orderFormNewDocumentNumberRuValidationEnabled
        || parentVm.citizenship !== 168
        || G2S.config.marketCode !== 'ru') {
        return true;
    }

    if (parentVm.validationConstraints.minDocumentValidTill.isSameOrAfter(moment(parentVm.birthday).add(14, 'y').add(3, 'M'))
        && ((/[0-9IVX]{1,4}[а-яА-Я]{2}[0-9]{6}$/).test(value) || (/^[a-zA-Zа-яА-Я]{2}[0-9]{6}$/).test(value))) {
        return false;
    }

    return true;
}

export function validateCertificateSymbol(value, parentVm: flights.PassengerModel): boolean {
    if (!parentVm.validationConstraints.orderFormNewDocumentNumberRuValidationEnabled
        || parentVm.citizenship !== 168) {
        return true;
    }
    // only < 14 year 3 month
    if (parentVm.validationConstraints['documentBirthCertificate']) {
        if ((((/^[0-9]{6}$/).test(value) || (/[a-zA-Zа-яА-Я][0-9]{6}$/).test(value)) && !(/^[0-9IVX]{1,4}[а-яА-Я]{2}[0-9]{6}$/).test(value))
            && parentVm.validationConstraints.minDocumentValidTill.isBefore(moment(parentVm.birthday).add(14, 'y').add(3, 'M'))) {
            return false;
        }
    }

    return true;
}

export function validateCertificateArmenia(value, parentVm: flights.PassengerModel): boolean {
    if (!parentVm.validationConstraints.orderFormNewDocumentNumberRuValidationEnabled
        || parentVm.citizenship !== 168) {
        return true;
    }

    if (!parentVm.validationConstraints['documentBirthCertificate'] && parentVm.validationConstraints['documentPassportRu']) {
        if (parentVm.validationConstraints.minDocumentValidTill.isSameOrAfter(moment(parentVm.birthday).add(14, 'y'))
            && (/^[0-9IVX]{1,4}[а-яА-Я]{2}[0-9]{6}$/).test(value)) {
            return false;
        }
    }

    return true;
}

export function validateDocumentForeign(value, parentVm: flights.PassengerModel): boolean {
    if (!parentVm.validationConstraints.orderFormNewDocumentNumberRuValidationEnabled
        || parentVm.citizenship !== 168
        || G2S.config.marketCode !== 'ru') {
        return true;
    }

    if (!parentVm.validationConstraints['documentPassportRu']) {
        if (value.length !== 9 || !(/^\d+$/).test(value)) {
            return false;
        }
    }

    return true;
}

/**
 * Custom validator for vuelidate.
 */
export function validateBirthday(value, parentVm: flights.PassengerModel): boolean {
    if (value) {
        const d = moment(value);

        if (d.isValid()) {
            if (d.isAfter(parentVm.validationConstraints.maxDateOfBirth) || d.isBefore(parentVm.validationConstraints.minDateOfBirth)) {
                return false;
            }
        }
    }

    return true;
}

/**
 * Custom validator for vuelidate.
 */
export function validateBirthdayMin(value, parentVm: flights.PassengerModel): boolean {
    if (value) {
        const d = moment(value);

        if (d.isValid()) {
            if (d.isBefore(parentVm.validationConstraints.minDateOfBirth)) {
                return false;
            }
        }
    }

    return true;
}

/**
 * Custom validator for vuelidate.
 */
export function isCitizenshipAccept(value, parentVm: flights.PassengerModel): boolean {
    if (value != undefined && value) {
        const isCitizenIncludes = parentVm.validationConstraints.countriesCitizenshipConstrain.includes(value);
            
        return parentVm.validationConstraints.isRequiredCitizenshipConstrains ? isCitizenIncludes : !isCitizenIncludes;
    }
    return true;
}

/**
 * Custom validator for vuelidate.
 */
export function isCitizenshipFilled(value: any): boolean {
    return Boolean(value);
}

/**
 * Custom validator for vuelidate.
 */
export function validateExchangeDesiredDate(value, parentVm): boolean {
    return parentVm.category == 'flight_change' && G2S.config.marketCode == 'ru' ? moment(value, 'YYYY-MM-DD').isAfter(moment()) : true;
}

/**
 * Custom validator for vuelidate.
 */
export function onlyOneUniqueCard(value: any) {
    if (value === null || value === '') {
        return true;
    }

    const cards = (<flights.ReservationModel>this.reservation).passengers.map(x => x.card);
    const sameCards = cards.filter(x => x == value);

    if (sameCards && sameCards.length > 1) {
        return false;
    } else {
        return true;
    }
}

/**
 * Custom validator for vuelidate.
 */
export function cardAirlineIdRequiredIfCardNotEmpty(value: any, passenger: flights.PassengerModel) {
    if (passenger.card === null || passenger.card === '') {
        return true;
    }

    if (passenger.card && (value === null || value === '')) {
        return false;
    } else {
        return true;
    }
}

export function validateTrainSelectedPlaces(value: api.TrainPlace[]) {
    if (value.length === this.reservation.passengersWithSeatCount) {
        return true;
    }

    return false;
}
